import { Container, Graphics, Text } from "pixi.js";

import type { Data } from "@colyseus/schema";
import type { SimReconciler } from "@colyseus/sdk";

import type { HelmInput, Ship } from "../../../shared/ArenaState";
import { canTurbo, DEATH_LINGER_SECONDS } from "../../../shared/Battle";
import { ZOOM_COMPENSATION } from "../../../shared/BoatTree";
import { defaultLoadout, Fitout, Loadout, resolveFitout, STARTING_FITOUT } from "../../../shared/Fitout";
import { Cannon, rosterFor, TRIGGER_INTERVAL } from "../../../shared/Gunnery";
import { advance, ARENA_SIZE, hullSpeed, shortestAngle, TURN_RATE, turnScaleOf, wrapCoord, wrapDelta } from "../../../shared/Sailing";
import { Boat } from "../boats/Boat";
import { Game } from "../Game";
import { BattleConnection } from "../net/BattleConnection";
import { LootField } from "../sea/LootField";
import { type Hull, PlunderField } from "../sea/PlunderField";
import { SeaLife } from "../sea/SeaLife";
import { ShotField } from "../sea/ShotField";
import { WakeField } from "../sea/WakeField";
import { Water } from "../sea/Water";
import { Leaderboard } from "../ui/Leaderboard";
import { Minimap } from "../ui/Minimap";
import { Growth } from "../ui/Growth";
import { SprayField } from "../sea/SprayField";
import { WaveTrain, Waves, waveHeightAt } from "../sea/Waves";
import { WindVane } from "../ui/WindVane";
import { Scene } from "./Scene";

declare global {
    interface Window {
        /** The sea this client is drawing, for the suite. See where it is set, in `enter`. */
        ironSailsSea?: () => WaveTrain[];
    }
}

/**
 * How close to the centre of the screen the pointer may get before it stops aiming. Without
 * it, a pointer resting on the player's own hull produces a jittering bearing out of nothing
 * but rounding, and the whole battery twitches with it.
 */
const POINTER_DEAD_ZONE = 14;

/**
 * Bit of `PointerEvent.buttons` that is the primary button — the left one on a mouse, and
 * the finger on a touchscreen. The trigger is read off `buttons` rather than counted up and
 * down from `pointerdown`/`pointerup`, because `buttons` is the live state of the hardware: a
 * button released while the window is unfocused corrects itself on the next move instead of
 * leaving the ship firing into an unwatched sea.
 */
const PRIMARY_BUTTON = 1;

/**
 * The keys that put the rudder over, and the one that lights the turbo, matched against
 * `KeyboardEvent.key` lowercased. See design/Controls.md.
 *
 * **Two spellings of the same three controls**, because they are the same three controls: the
 * arrow cluster and the left hand's WAD sit under whichever hand is not on the mouse, and a
 * player who reaches for one should not have to discover the other. `S` is deliberately not
 * bound — there is no reverse, and a key that quietly does nothing is better than a key that
 * does something nobody asked for.
 *
 * `key` rather than `code`, lowercased, for the reason the header's shortcuts used: it is the
 * character the key produced, so a layout that puts these letters elsewhere steers the way the
 * player actually typed, and Caps Lock does not jam the rudder.
 */
const PORT_KEYS = ["a", "arrowleft"];
const STARBOARD_KEYS = ["d", "arrowright"];
const TURBO_KEYS = ["w", "arrowup"];
/**
 * Not a control — a **development** key, which is why it is not in `_isControlKey` and not in
 * design/Controls.md. It shows the wave field as a grid of dots over the water: the height the
 * *CPU* works out, drawn over the water the shader draws, which is the only way to see the two
 * agree. See design/Testing.md.
 */
const WAVE_DEBUG_KEY = "f9";

/** How far apart the debug grid's dots sit, in world pixels, and how big one gets at full crest. */
const WAVE_DOT_SPACING = 80;
const WAVE_DOT_RADIUS = 7;

/**
 * How far ahead of the hull the rudder is allowed to wind the course, in radians.
 *
 * The rudder is held, but the wire carries a **heading**, not a rate: `steer` has always been
 * the course a client wants, and the server swings the hull onto it at its own TURN_RATE
 * (design/Networking.md). So a held key winds the wanted course round at the rate the hull
 * could actually follow — and this caps how far that course may run ahead of where the ship
 * has really got to.
 *
 * Without the cap, a stalled frame, a slow hull, or a key held through a full circle would
 * leave the target most of a turn ahead, and letting go would not stop the ship: it would keep
 * coming about for as long as it took to unwind the lead. A quarter radian is enough to keep
 * the hull hard over and short enough that releasing the key stops the turn on the next frame.
 */
const RUDDER_LEAD = 0.25;

/**
 * How fast the camera slides onto a new hull's zoom, as a fraction of the remaining gap per
 * second, and the gap below which it simply arrives.
 *
 * It eases rather than cuts because a hull upgrade is a **reward**, and a screen that jumps
 * scale reads as a glitch — the player loses track of which of the boats on it is theirs at the
 * exact moment they were meant to enjoy being bigger. About a second is long enough to read as
 * the camera pulling back and short enough that the wider view is there before the fight moves.
 *
 * The epsilon is not decoration: an exponential ease never actually arrives, and every frame it
 * has not arrived re-lays out every world-space layer for a change of a ten-thousandth.
 */
const ZOOM_RATE = 3;

/**
 * How much of the arena the viewport may cover before the camera stops pulling back, as a
 * fraction. A tenth in hand over the bound `_framedZoom` derives, for the hull's own drawn extent
 * either side of the centre the bound is about.
 */
const WRAP_MARGIN = 0.9;
const ZOOM_EPSILON = 0.001;

declare global {
    /** Vite's build-mode flag. Declared here rather than pulling `vite/client` into every config. */
    interface ImportMeta {
        env?: { DEV?: boolean };
    }
}

/**
 * What the prediction simulates, as the SDK sees it.
 *
 * `hull` is the predicted ship in **unwrapped** world coordinates — it accumulates rather than
 * folding at the edge of the sea, so nothing the SDK does to it ever subtracts across the seam.
 *
 * `truth` is the arena's own copy, deliberately **nested inside a plain object**. A decoded schema
 * instance placed at the top level of the world would be *auto-bound*: the SDK would mirror its
 * scalars itself and put the wrapped coordinates straight back into the arithmetic this whole
 * arrangement exists to keep them out of. Nesting keeps it opaque, and `adopt` does the seeding by
 * hand. See design/Networking.md, "On the client".
 */
interface PredictedWorld {
    hull: { x: number; y: number; heading: number };
    truth: { ship: Ship | null };
}

/**
 * What the SDK smooths and interpolates: the predicted hull, in the continuous space above. A type
 * rather than an interface because the SDK constrains a pose to `Record<string, number>`, which an
 * interface does not satisfy without an index signature.
 */
type PredictedPose = { x: number; y: number; heading: number };

/**
 * The rollback handle for the player's own hull. `Data<HelmInput>` rather than `HelmInput` because
 * what a step is handed is the decoded wire frame — the exact value the arena reads, which is what
 * makes a quantized angle replay identically on both ends.
 */
type HullReconciler = SimReconciler<Data<HelmInput>, PredictedPose, PredictedWorld>;

/**
 * How long a misprediction takes to fade out of the drawn position, in ms.
 *
 * The old dial was a fraction per second (`RECONCILE_RATE`, 3/sec) applied to a soft ease toward a
 * patch-old server position. This is not that number rescaled: the correction being smoothed now is
 * the residue **after** the unacknowledged inputs have been replayed on top of the server's answer,
 * so it is small and short-lived where the old one was a standing chase. Roughly one patch, so a
 * correction has faded before the next one arrives.
 */
const RECONCILE_SMOOTH_MS = 65;
/**
 * Beyond this correction, in pixels, the drawn position pops instead of fading — a teleport rather
 * than an error to glide across.
 *
 * **It only has to cover spawning now.** The old `RECONCILE_SNAP` (350) was sized mostly around the
 * seam, where a hull crossing the edge of the world made the server and the prediction disagree by
 * most of an arena; the prediction runs in unwrapped coordinates that never fold, so that
 * disagreement no longer exists. See design/Networking.md, "On the client".
 */
const RECONCILE_SNAP = 350;
/**
 * How far the arena and the prediction may disagree, in world pixels, before a development build
 * says so on the console. About a hull's length: closer than that is ordinary latency, further is
 * the two copies of `advance()` having stopped agreeing about something.
 */
const DIVERGENCE_WARN = 120;

/** How fast other boats are pulled onto their latest server position, per second. */
const REMOTE_LERP_RATE = 12;
/** Beyond this error, in pixels, a remote boat is teleported — it respawned or joined. */
const REMOTE_SNAP = 500;


const STATUS_CONNECTING = "Putting to sea...";
const STATUS_FAILED = "Could not reach the arena.\nIs `npm run localbattle` running?";

/** A ship as this client is drawing it — smoothed, and behind the server by design. */
interface ShipView {
    boat: Boat;
    x: number;
    y: number;
    heading: number;
    /**
     * The levels the deck was last built from, as a string. Ships grow mid-battle now
     * (design/Evolution.md), so every frame has to ask whether the boat on screen is still the
     * boat the server is describing — and the cheapest honest answer is to compare what the
     * three wire fields spell.
     */
    rig: string;
}

/** Pointer events that carry a `buttons` field this scene trusts for the trigger. */
const POINTER_EVENTS = ["pointermove", "pointerdown", "pointerup", "pointercancel"] as const;

/**
 * The arena. Sails the player's boat against everyone else's on a looping sea.
 *
 * Three positions exist for the local boat and the difference matters: the **server's**,
 * which is authoritative but a patch old; the **predicted** one, which this scene
 * integrates from the player's own input every frame so steering feels instant; and the
 * **camera**, which is simply the predicted one. Other boats have no prediction — they
 * are eased towards whatever the server last said.
 *
 * See design/Networking.md.
 */
export class BattleScene extends Scene {
    private readonly _connection = new BattleConnection();
    /**
     * Everything that has a **world position**: the loot, the hulls and the shots. It is one
     * container so the camera's zoom is one `scale` on one node rather than three that could
     * drift apart, and so the chrome outside it — the map, the leaderboard, the status text —
     * keeps its own size whatever the hull is doing. The sea is not in here either; see
     * `_applyZoom`.
     */
    private readonly _world = new Container();
    private readonly _shipsLayer = new Container();
    private readonly _views = new Map<string, ShipView>();
    private readonly _lootField = new LootField();
    private readonly _shotField = new ShotField();
    private readonly _plunderField = new PlunderField();
    private readonly _minimap = new Minimap();
    private readonly _leaderboard = new Leaderboard();
    /** The wind arrow, above the map. Reads `ArenaState.wind` and draws nothing else. */
    private readonly _windVane = new WindVane();
    /**
     * The wave field, as dots. Off unless somebody asks for it — see `_drawWaveDebug`, and
     * WAVE_DEBUG_KEY above.
     */
    private readonly _waveDots = new Graphics();
    /**
     * The sea, which is **this client's own** and on the wire nowhere (design/Water.md). Drawn by
     * the water, read by every hull that rides on it and by the coins.
     */
    private readonly _waves = new Waves();
    private readonly _growth = new Growth();

    private _water: Water | null = null;
    private _seaLife: SeaLife | null = null;
    private _wakeField: WakeField | null = null;
    /**
     * The water the bows throw. Above the sea rather than in it — spray is the one effect here
     * that is *airborne*, so the swell must not warp it. See src/scripts/sea/SprayField.ts.
     */
    private readonly _sprayField = new SprayField();
    private _status: Text | null = null;

    /**
     * Where this client believes its own boat is, in **wrapped world space** — what everything on
     * screen is laid out against, and authoritative for the camera.
     *
     * It is a copy rather than the prediction itself: `_reconciler` keeps the hull in *unwrapped*
     * coordinates that never fold, so that the SDK's smoothing and its correction arithmetic never
     * subtract two numbers across the seam (design/Networking.md). This is that
     * pose folded back into the sea, refreshed once a frame, and it is the only place the fold
     * happens.
     */
    private _predicted = { x: 0, y: 0, heading: 0 };
    /**
     * The player's own hull, predicted and rolled back: every helm command is applied the moment
     * it is sent, and when the arena's answer arrives the hull is rewound to it and the commands
     * it has not acknowledged yet are replayed on top.
     *
     * `predict.sim` rather than `predict.reconciler` because the pose is ours to choose that way,
     * which is the whole of how the torus is kept out of the SDK's arithmetic. Null until this
     * client has seen its own ship — there is nothing to reconcile against before that.
     */
    private _reconciler: HullReconciler | null = null;
    /**
     * Set the moment this scene is torn down. Anything that comes back from the arena *after* that
     * — an upgrade the player asked about a fraction of a second before being sunk — has to find
     * out, because writing to chrome that has been removed is how a tidy exit turns into an error
     * in the console.
     */
    private _gone = false;
    private _hasSpawned = false;
    /**
     * The clock reading at which this client noticed its own hull had left the sea, or null while
     * it is still afloat. Set once and never cleared: it is both the latch that stops `onSunk`
     * firing twice — the handler hands the scene over, and firing it twice would change scene
     * twice — and the start of the count the player is held at sea for. See `_checkSunk`.
     */
    private _sunkAt: number | null = null;
    /** Set when `onSunk` has been handed over, so the last frame of the linger only does it once. */
    private _ashore = false;
    /**
     * Set the frame the arena stops answering, and never cleared. A latch for the same reason
     * `_ashore` is one: `onLost` hands the scene over, and firing it twice would put two notices up
     * over two scene changes. It also **freezes the scene** — see `update`.
     */
    private _lost = false;
    /**
     * The last purse this client saw, kept because the ship it came from may already be gone.
     *
     * On a sinking the hull leaves `ships` on the step it is taken, and on a lost connection the
     * patches simply stop — so the number to bank is the last one that arrived rather than one
     * read at the end. That is the whole reason the purse is state rather than a farewell message
     * (design/Networking.md): by the time a life is over, the client already has it.
     */
    private _purse = 0;
    private _targetHeading = 0;

    private _cameraX = 0;
    private _cameraY = 0;
    /**
     * The scale the world is drawn at, and the scale it is heading for. Two numbers because a
     * hull upgrade moves the target and `_workZoom` walks the camera onto it — see ZOOM_RATE.
     *
     * Both start at the spawn hull's, so the first frame of a battle is already framed for the
     * boat everybody starts in rather than easing in from 1 while the player is reading the sea.
     */
    private _zoom = cameraZoomOf(STARTING_FITOUT);
    private _targetZoom = cameraZoomOf(STARTING_FITOUT);

    private _pointerX = 0;
    private _pointerY = 0;
    private _hasPointer = false;
    /** Where the player is pointing, in world radians. The direction every gun swings toward. */
    private _aimHeading = 0;

    /** Which way the rudder is over: -1 to port, +1 to starboard, 0 amidships. */
    private _rudder = 0;
    /** The steering keys currently down, so releasing one of two still leaves the other over. */
    private readonly _keysDown = new Set<string>();

    /** Whether the player is asking for turbo — the key, before the score is consulted. */
    private _turboHeld = false;
    /** Whether it is actually lit: asked for, and affordable. What the prediction runs on. */
    private _turboLit = false;
    /** Whether the trigger is down. Held, not clicked — see TRIGGER_INTERVAL. */
    private _fireHeld = false;
    /**
     * A press that has not been acted on yet. `_fireHeld` alone is the live state of the
     * hardware, which is what a *held* trigger needs — but a click quick enough to go down and up
     * between two frames would never be seen as held at all, and the shot the player asked for
     * would simply not happen. The latch is set on the `pointerdown` and consumed by the next
     * frame, so every press is worth at least one look at the battery.
     */
    private _firePressed = false;

    /**
     * Seconds of scene time since the battle began. The clock the client's own reload and
     * trigger predictions run on, exactly as the room's `_simTime` is the server's.
     */
    private _clock = 0;
    /**
     * When each of this ship's guns is loaded again, in scene time, keyed by **cannon slot**.
     * The client's own copy of a clock the server also keeps: a battery that waited for the
     * arena to confirm a shot before offering the next gun would feel broken at any latency,
     * so the pick runs locally and the server declines anything early (design/Networking.md).
     *
     * Not cleared on a refit, for the reason the server's is not: promoting a cannon should not
     * reload it for free, and a slot that has left the deck simply stops being read.
     */
    private readonly _gunReadyAt = new Map<number, number>();
    /** When the ship may fire its next ball at all, in scene time. See TRIGGER_INTERVAL. */
    private _triggerReadyAt = 0;
    /**
     * Slots whose reload the arena has confirmed and this client has not re-armed yet, drained
     * at the top of the next frame. See `_confirmReload` for why the correction exists at all,
     * and why it cannot be applied where the message arrives.
     */
    private readonly _confirmedReloads = new Set<number>();

    /**
     * What the player's own ship currently is. The prediction runs `advance()` with this hull's
     * speed and turn multipliers, so a Galleon is predicted as a Galleon rather than drifting
     * away from the server every step. Rebuilt from the wire whenever the levels change.
     */
    private _fitout: Fitout = STARTING_FITOUT;

    private _width = 0;
    private _height = 0;
    /**
     * The viewport in **world** pixels — the screen divided by the zoom. This is the size every
     * layer inside `_world` is told about, because they all work in world units and then get
     * scaled once on the way to the screen: half of it is where the player's own hull is drawn,
     * and its edges are what those layers cull against.
     */
    private _viewWidth = 0;
    private _viewHeight = 0;

    private readonly _onPointer = (event: PointerEvent): void => {
        // A pointer on the header or the growth panel is working the chrome, not the sea.
        // Without this, reaching for an upgrade drags the whole battery round to the top-left
        // corner and, since the press sets the primary bit, fires a ball on the way past. The
        // panel is `pointer-events: none` except on its buttons, so the sea behind the empty
        // parts of it still aims. The aim simply holds where it was. See design/UI.md.
        if (event.target instanceof Element && event.target.closest(".toolbar, .growth, .notice")) {
            this._fireHeld = false;
            this._firePressed = false;
            return;
        }

        this._pointerX = event.clientX;
        this._pointerY = event.clientY;
        this._hasPointer = true;
        this._fireHeld = (event.buttons & PRIMARY_BUTTON) !== 0;
        // The press itself, latched, so a click shorter than a frame still fires. See
        // `_firePressed` — a mouse that goes down and up inside 16ms is an ordinary click.
        if (event.type === "pointerdown" && this._fireHeld) this._firePressed = true;
    };

    /**
     * The rudder and the turbo, both held. Down and up rather than a `keypress`, because these
     * are states of the hand rather than events: what the ship wants to know is whether the key
     * is *currently* over, and the operating system's auto-repeat rate is not a rate this game
     * gets to choose. See design/Controls.md.
     */
    private readonly _onKeyDown = (event: KeyboardEvent): void => {
        if (event.ctrlKey || event.altKey || event.metaKey) return;
        // A letter typed into a field is somebody entering a name, not an order to the helm.
        if (event.target instanceof Element && event.target.closest("input, textarea, [contenteditable]")) return;

        const key = event.key.toLowerCase();
        if (key === WAVE_DEBUG_KEY) {
            this._waveDots.visible = !this._waveDots.visible;
            event.preventDefault();
            return;
        }

        if (!this._isControlKey(key)) return;

        // The arrows scroll the page and space would too; a bound key belongs to the ship.
        event.preventDefault();
        this._keysDown.add(key);
        this._readKeys();
    };

    private readonly _onKeyUp = (event: KeyboardEvent): void => {
        const key = event.key.toLowerCase();
        if (!this._keysDown.delete(key)) return;

        event.preventDefault();
        this._readKeys();
    };

    /**
     * A window that loses focus keeps no `pointerup` or `keyup` promise — alt-tabbing with the
     * rudder over would otherwise leave the ship circling, the turbo draining the score into an
     * unwatched sea, and the guns firing at nothing.
     */
    private readonly _onBlur = (): void => {
        this._keysDown.clear();
        this._readKeys();
        this._fireHeld = false;
        this._firePressed = false;
    };

    /**
     * Called when the player's battery changes — on spawn, and on any upgrade that fits or
     * improves a gun. The scene does not know there is a header; `main.ts` hands the row over,
     * the same way it hands the gun buttons back as `fire`.
     */
    public onBattery: ((cannons: readonly Cannon[]) => void) | null = null;

    /**
     * Called on every ball this ship actually asks for, with the gun that fired it. The header's
     * battery strip runs that gun's reload from it — on the client's own clock, like the trigger
     * itself, so the strip is in step with what the player can do next rather than with what the
     * arena has got round to confirming.
     */
    public onFired: ((cannon: Cannon) => void) | null = null;

    /**
     * What this life has earned so far, in doubloons — handed up so the header can show it. The
     * scene has no idea there is a header, the same way the header has no idea there is an arena.
     */
    public onPurse: ((doubloons: number) => void) | null = null;

    /**
     * What this life earned, handed over **once**, as the scene is torn down.
     *
     * Fired from `destroy` rather than from any of the three things that end a life, because all
     * three end it by tearing the scene down and only one of them is a place a number can still be
     * read: a sinking takes the hull out of `ships` before anything here notices, so the purse has
     * to have been latched already. One call site is also one chance to bank twice, which is none.
     */
    public onEarned: ((doubloons: number) => void) | null = null;

    /**
     * Called once, when this client's own ship is no longer in the sea — it was sunk by a ball
     * that found it under the death line (design/Battle.md). A life is over, so the scene is
     * finished: `main.ts` puts the player back on the main menu, which is where the next one
     * starts from.
     *
     * A hook rather than a scene change made here, for the reason `onBattery` is one: the scene
     * has no idea what else exists, and which screen follows a sinking is the caller's decision.
     */
    public onSunk: (() => void) | null = null;

    /**
     * Called once, when the connection to the arena dies without this client asking it to. The
     * ship is not lost — the arena keeps it as a ghost (design/Battle.md) — but this session's
     * hold on it is, and there is no way back to it.
     *
     * Separate from `onSunk` because they are different events that happen to end the same way.
     * A sinking is the game working; this is the game stopping. The player is owed an explanation
     * for the second and not the first, and only the caller knows how to give one.
     */
    public onLost: (() => void) | null = null;

    constructor(
        private readonly _playerName: string,
        private readonly _loadout: Loadout = defaultLoadout()
    ) {
        super();
    }

    public enter(width: number, height: number): void {
        // Before anything is sized: `_framedZoom` reads them, and a viewport of zero would put
        // the wrap floor at zero and hand every world-space layer an infinite one.
        this._width = width;
        this._height = height;

        this._water = new Water(width, height, Game.app.renderer);

        // Sea life is the one thing that goes *inside* the water: it is below the surface, so
        // it belongs under the wave overlay and inside the displacement filter. It is also
        // decoration and nothing else — the server does not know it exists, so no amount of
        // warp can move a thing the player might have been steering at. See design/SeaLife.md.
        // At the **world** size, not the screen's: the school is scaled with the boats (see
        // `_applyZoom`), so a fresh animal placed anywhere in the viewport has to be placed in
        // the coordinates that viewport is drawn from, or the whole school starts bunched into
        // one corner of a zoomed-out sea.
        this._seaLife = new SeaLife(width / this._framedZoom(), height / this._framedZoom());
        this._water.underwater.addChild(this._seaLife.container);

        // The trail is the other half of that argument and lands on the other side of the wave
        // overlay: foam is *on* the sea rather than under it, so it goes in `surface` — still
        // inside the filter, so the swell carries it. Like the school it is scaled with the
        // world and given the viewport in world pixels, because a mark is measured against a
        // hull. See design/Water.md.
        // The trail is **not** added to the scene: it is handed to the water, which renders it
        // into the map the whole sea is then drawn through. Foam and warp both come out of that
        // one image, so a mark is part of how the water looks rather than a sprite over it. Like
        // the school it is scaled with the world and given the viewport in world pixels, because
        // a mark is measured against a hull. See design/Water.md.
        this._wakeField = new WakeField(width / this._framedZoom(), height / this._framedZoom());
        this._water.setTrail(this._wakeField.field, this._wakeField.displacementStrength, this._wakeField.foamStrength);

        // Boats, loot and shots are siblings of the water, not children: everything inside
        // the water container goes through the displacement filter, which would ripple the
        // hulls and drag the coins away from where they actually are. Loot goes under the
        // ships, so a hull is never hidden by the thing it is sailing over; cannonballs go
        // over them, because a ball in the air is in front of the deck it is crossing.
        this._world.addChild(
            this._lootField.container,
            // Under the hulls, so a plume can never hide the ship that threw it — rule 8, and the
            // fan opens outside the silhouette anyway.
            this._sprayField.container,
            this._shipsLayer,
            this._plunderField.container,
            this._shotField.container
        );
        // Over everything, because it is a development overlay and being in the way is the job.
        this._waveDots.visible = false;
        this._world.addChild(this._waveDots);
        this._container.addChild(
            this._water.container,
            this._world,
            this._minimap.container,
            this._leaderboard.container,
            this._windVane.container
        );

        // Shots arrive as broadcasts rather than as state, so they are handed straight to the
        // field that flies them. Both are set before `join`, or the first broadside of a busy
        // arena lands in a listener that does not exist yet.
        this._connection.onShot = (shot) => {
            this._shotField.add(shot);
            // The ball, the kick and the muzzle flash are one event, so they land together —
            // including on the screen that asked for the shot. Predicting the recoil off the
            // press instead would start the rotation lock a round trip before the arena's, and
            // the two would draw the barrel at different bearings for as long as the lock lasts.
            // The id goes through because it seeds which flash comes up, so every client watching
            // this broadside shows the same flame — see src/scripts/boats/MuzzleFlashes.ts.
            this._views.get(shot.from)?.boat.fired(shot.slot, shot.id);
            if (shot.from === this._connection.sessionId) this._confirmedReloads.add(shot.slot);
        };
        // Before `join`, like the two above: a socket that fails on the very first frame has to
        // find a listener already there.
        this._connection.onLost = (detail) => {
            // The reason goes to the console and the player gets a sentence. What went wrong is
            // a close code, which is worth having when debugging and worth nobody's time in a
            // dialog over a frozen sea.
            console.warn("[battle] connection lost —", detail);
            if (this._lost) return;

            this._lost = true;
            this.onLost?.();
        };
        this._connection.onHit = (hit) => {
            this._shotField.land(hit);
            // The points have already moved — the server did it in the step that detected the
            // hit, and the score patch is on its way. This is only the sight of it. See
            // src/scripts/sea/PlunderField.ts.
            this._plunderField.open(hit);
        };

        // Down until the local hull is really in the sea — see `Growth.visible`.
        this._growth.visible = false;
        this._growth.mount();

        this._status = new Text({
            text: STATUS_CONNECTING,
            anchor: 0.5,
            style: { fontFamily: "Grobold", fontSize: 20, fill: 0xf2e2c4, align: "center", lineHeight: 28 }
        });
        this._container.addChild(this._status);

        // Pointer events on the window rather than the canvas, so aiming survives the pointer
        // crossing the DOM toolbar. `pointermove` covers mouse and touch-drag alike;
        // `pointerdown` lets a tap lay the guns without needing to drag first, and the
        // up-and-cancel pair is what lifts the trigger.
        for (const type of POINTER_EVENTS) window.addEventListener(type, this._onPointer);
        // The helm is on the keyboard, and on `window` for the same reason the pointer is: the
        // canvas never takes focus and neither does the header, so a listener anywhere narrower
        // would go deaf the moment the player clicked something. See design/Controls.md.
        window.addEventListener("keydown", this._onKeyDown);
        window.addEventListener("keyup", this._onKeyUp);
        window.addEventListener("blur", this._onBlur);

        this.resize(width, height);

        this._connection.join(this._playerName, this._loadout).catch((error) => {
            console.error("[battle] join failed", error);
            if (this._status) this._status.text = STATUS_FAILED;
        });
    }

    /**
     * What the arena's round trip currently measures, for the frame counter to show — null before
     * there is a reading. Straight through from the connection; the scene owns it only because the
     * scene is what main.ts has a handle on.
     */
    public get latency(): { rtt: number; jitter: number } | null {
        return this._connection.latency;
    }

    public override update(deltaSeconds: number): void {
        this._clock += deltaSeconds;
        // Frozen the moment the arena stops answering. Everything below this line reads a state
        // that has stopped arriving, so running it would sail the player's boat on over a still
        // sea, on their own prediction, for as long as the notice is up — which reads as the game
        // half-working rather than as the connection being gone. See `onLost`.
        if (this._lost) return;

        // Before anything else touches the sea: the last frame of a sinking hands the scene over,
        // and whoever takes it may destroy this one on the spot. Everything below would then be
        // running on torn-down containers. See `_checkSunk`.
        if (this._checkSunk()) return;

        // Everything from here to `_syncViews` is the helm, and a captain whose hull is on the
        // bottom has none. They are watching the sea now: it keeps running below, but nothing
        // this player does reaches it, and nothing is put on the wire for a ship the arena has
        // already torn down. See DEATH_LINGER_SECONDS.
        if (this._sunkAt === null) {
            // First, while the clock is freshly advanced — see `_confirmReload`.
            this._confirmReloads();

            this._readPointer();
            this._workRudder(deltaSeconds);

            // **Before anything reads the prediction.** This is what advances the fixed-step clock,
            // sends the helm for each step that is due, replays the unacknowledged commands on top of
            // the newest answer from the arena, and decays what is left of the last correction. Reading
            // the predicted hull before it would draw the previous frame's.
            this._connection.steer(this._targetHeading);
            // A direction, never a gun angle. The arena runs the same traverse off it and owns the
            // answer; what is drawn here is this client's copy of the same sum.
            this._connection.aim(this._aimHeading);
            // The key is sent, not the prediction: the server has its own copy of the score
            // and decides for itself whether the hull can afford to burn.
            this._connection.turbo(this._turboHeld);
            this._connection.flush();
        }

        this._syncViews();
        this._syncRigs();
        // After the rigs, which is what moves the target, and before anything is placed: every
        // world-space layer this frame has to be laid out against one zoom.
        this._workZoom(deltaSeconds);
        this._advanceLocal();
        this._advanceRemotes(deltaSeconds);
        this._moveCamera(deltaSeconds);
        this._placeBoats(deltaSeconds);
        // After the boats, because `_placeBoats` is what swings the guns: the cannon a press
        // picks has to be the one at the bearing this frame actually drew. Silent once the hull
        // is gone, like the rest of the helm — `_workTrigger` would find no view to fire from
        // anyway, and this says why rather than relying on that.
        if (this._sunkAt === null) this._workTrigger();

        // The sea, handed to the thing that draws it. One `Waves` on this client, read by the
        // shader and by the overlay below, so the two cannot disagree. It was moved on back in
        // `_moveCamera`, which is the one place that knows how far the water slid past.
        this._water?.setWaves(this._waves.trains);
        if (this._waveDots.visible) this._drawWaveDebug();

        this._lootField.update(this._connection.loot, this._waves.trains, this._cameraX, this._cameraY, deltaSeconds);
        // Before the shots so a ball crosses in front of the coins, and after `_placeBoats`,
        // which is what `_locate` reads: the plunder lands on the hull as it is drawn this
        // frame, not on where it was at the start of it.
        this._plunderField.update(this._locate, this._cameraX, this._cameraY, deltaSeconds);
        this._shotField.update(this._cameraX, this._cameraY, deltaSeconds);
        this._minimap.update(this._connection.ships, this._connection.sessionId);
        // The weather is the arena's, not a ship's — one number for the whole sea, drawn straight
        // off the state. See design/Wind.md.
        this._windVane.update(this._connection.wind, this._predicted.heading);
        this._leaderboard.update(this._connection.ships, this._connection.sessionId, deltaSeconds);
        this._updateGrowth();
        this._seaLife?.update(deltaSeconds);
        // After `_placeBoats`, which is what lays the fresh foam: a mark laid this frame is
        // drawn where it was laid rather than a frame behind the stern that laid it.
        this._wakeField?.update(deltaSeconds);
        this._sprayField.update(deltaSeconds);
        this._water?.update(deltaSeconds);

    }

    public override resize(width: number, height: number): void {
        this._width = width;
        this._height = height;

        // Screen-space furniture takes the window as it is. The sea is in here rather than in
        // `_applyZoom` for the reason given there: its plate is measured in screen pixels.
        this._water?.resize(width, height);
        this._minimap.resize(width, height);
        this._leaderboard.resize(width, height);
        this._windVane.resize(width, height);
        this._status?.position.set(width / 2, height / 2);

        // And everything with a world position takes the window divided by the zoom.
        this._applyZoom();

        // The one thing on `window`, and it is here so a test can see the sea.
        //
        // The waves used to be on the wire, so the suite read them off an observer's room the way
        // it reads a hull's position. They are this client's own now (design/Water.md), and
        // there is no other way in: a Playwright test can screenshot a canvas and it
        // can talk to the arena, and nothing else. Two tests need it and both are worth keeping —
        // the parity check that the shader and `waveHeightAt` are still one formula, and the check
        // that a hull is drawn riding the water. See design/Testing.md.
        //
        // A copy, not the live array: a test that held these objects would watch them change under
        // it mid-measurement, which is a mistake this suite has already made once.
        window.ironSailsSea = () => this._waves.trains.map((train) => ({ ...train }));

    }

    public override destroy(): void {
        this._gone = true;
        // Before anything else is unwound: this is the one place every way out of a battle passes
        // through — sunk, Leave, and a connection that died — so it is the one place the run's
        // earnings are banked. See `onEarned`.
        this.onEarned?.(this._purse);
        for (const type of POINTER_EVENTS) window.removeEventListener(type, this._onPointer);
        window.removeEventListener("keydown", this._onKeyDown);
        window.removeEventListener("keyup", this._onKeyUp);
        window.removeEventListener("blur", this._onBlur);

        // Real DOM, so it does not go with the scene container the way everything else does.
        this._growth.destroy();
        this._sprayField.destroy();

        delete window.ironSailsSea;

        this._connection.leave();
        // The boats' containers go with the scene container; the map is just bookkeeping.
        this._views.clear();

        super.destroy();
    }

    /**
     * Notices that this life has ended, holds the player at sea to watch it, and — once the count
     * has run out — hands the scene over. Returns true once the hand-over has happened, which is
     * the one frame `update` must not run another line after.
     *
     * The test is the ship's **absence from `ships`**, not the `killed` flag on the hit message.
     * The removal is the authoritative fact and the message is a convenience: a hit that sinks a
     * hull and the patch that takes it out of the sea are the same server step, so waiting for
     * the state is no slower, and reading the state cannot leave a player sailing an empty sea
     * because one broadcast went astray. `killed` is what lets the client know a *ball* did it,
     * which is what a death card would need — there is not one yet, so nothing reads it.
     *
     * **The wait is this client's and nobody else's.** The hull is out of the arena on the step
     * the ball landed and stays out; what lingers is the seat, the socket and this scene, so the
     * captain sees their own wreck settle and whoever sank it sail on rather than the menu
     * appearing where the sea was. The arena is built for it — a room counts its humans in
     * clients, not hulls, precisely so a captain watching their own sinking is not churn. Nothing
     * here goes back on the wire: `update` puts the whole helm behind the same latch.
     *
     * `_hasSpawned` guards the frames before the first patch, when there is legitimately no ship
     * for this session yet, and `_ashore` guards against a second hand-over once the scene is on
     * its way out — the handler is allowed to destroy this scene synchronously, so `update`
     * returns immediately after it and touches nothing else.
     */
    private _checkSunk(): boolean {
        if (this._ashore) return true;

        if (this._sunkAt === null) {
            if (!this._hasSpawned || this._connection.ships?.get(this._connection.sessionId)) return false;
            this._sunkAt = this._clock;
            // The deck plan goes with the deck. Every other piece of chrome that describes the
            // player's own ship takes itself down when there is no ship — the growth bar, the
            // minimap dot, the player's row on the board — and a battery strip left standing
            // through the count would be the only one still describing a hull on the bottom.
            // The purse stays: that is what the life earned, and it is about to be banked.
            this.onBattery?.([]);
        }

        if (this._clock - this._sunkAt < DEATH_LINGER_SECONDS) return false;

        this._ashore = true;
        this.onSunk?.();
        return true;
    }

    /**
     * How far through the wreck's settling this frame is: 0 on the frame the hull went down, 1 at
     * the end of the count. Always 1 when the linger is dialled to zero, which is the divide the
     * expression would otherwise do — and the right answer besides, since the wreck in that case
     * is never drawn at all.
     */
    private _founderedness(): number {
        if (this._sunkAt === null) return 0;
        if (DEATH_LINGER_SECONDS <= 0) return 1;

        return Math.min(1, (this._clock - this._sunkAt) / DEATH_LINGER_SECONDS);
    }

    /**
     * Turns the pointer's offset from screen centre into the direction the guns are laid on.
     *
     * The player's own hull is always at screen centre (design/Ships.md), so the offset *is* the
     * bearing from the ship to whatever is under the pointer — which is why the aim needs no
     * world coordinate and no ray into the sea. **Only the direction is read.** How far the
     * pointer is from the boat says nothing about how far a ball goes; that is the gun's own
     * `range`, and a target beyond it is simply out of reach. See design/Controls.md.
     */
    private _readPointer(): void {
        if (!this._hasPointer) return;

        const dx = this._pointerX - this._width / 2;
        const dy = this._pointerY - this._height / 2;
        if (Math.hypot(dx, dy) < POINTER_DEAD_ZONE) return;

        // Heading 0 is north and grows clockwise, so north is -y — hence the negated dy.
        this._aimHeading = Math.atan2(dx, -dy);
    }

    /**
     * Re-arms the player's own guns from the arena's account of when they actually fired, rather
     * than from when this client asked.
     *
     * **Without this a held trigger silently drops shots.** The reload is predicted locally so a
     * press answers at once, but the two clocks do not start together: the client's begins when
     * it *sends* and the server's when it *receives*, so the client's gun comes back a round trip
     * early, every time. Ask on that edge and the arena declines — in silence, because a "no" the
     * client cannot act on is a wasted packet (design/Networking.md) — and the ball simply never
     * happens. Measured on a local arena it cost between a third and a quarter of a held
     * trigger's shots, and the gap only widens with latency.
     *
     * The `shot` broadcast is the fix because it is the one message that says *the arena fired
     * this gun, now*. Re-arming from it puts the local clock **behind** the server's instead of
     * ahead, which is the safe direction: the trigger waits a few milliseconds longer than it
     * strictly must, and every request it does make is honoured.
     *
     * It is drained here, at the top of a frame, rather than in the message handler — and that
     * is the whole reason this is a queue and not a one-liner. `_clock` only advances in
     * `update`, so a handler running between two frames reads a timestamp up to a frame stale
     * and re-arms the gun *earlier* than the message arrived, which is the same bug again at a
     * sixtieth of the size. Draining against a clock that has just been advanced cannot be early.
     *
     * The header's bar is deliberately **not** re-armed with it. That is instant feedback for a
     * press, and restarting the animation when the confirmation lands would make it visibly
     * stutter at exactly the latency this is correcting for.
     */
    private _confirmReloads(): void {
        if (this._confirmedReloads.size === 0) return;

        const roster = this._views.get(this._connection.sessionId)?.boat.roster;
        for (const slot of this._confirmedReloads) {
            const cannon = roster?.find((gun) => gun.index === slot);
            if (cannon) this._gunReadyAt.set(slot, this._clock + cannon.reload);
        }

        this._confirmedReloads.clear();
    }

    /** Whether a key is one this scene has taken, so an unbound one is left to the browser. */
    private _isControlKey(key: string): boolean {
        return PORT_KEYS.includes(key) || STARBOARD_KEYS.includes(key) || TURBO_KEYS.includes(key);
    }

    /**
     * Reads the rudder and the turbo off the keys that are currently down. Both spellings of a
     * control count, and holding both directions at once is amidships rather than one of them
     * winning — the ship does what a player holding two opposite keys means, which is nothing.
     */
    private _readKeys(): void {
        const port = PORT_KEYS.some((key) => this._keysDown.has(key));
        const starboard = STARBOARD_KEYS.some((key) => this._keysDown.has(key));

        this._rudder = Number(starboard) - Number(port);
        this._turboHeld = TURBO_KEYS.some((key) => this._keysDown.has(key));
    }

    /**
     * Winds the wanted course round while the rudder is over.
     *
     * The wire carries a heading rather than a rate (design/Networking.md), so a held key has to
     * become a course: it advances at the speed this hull could actually follow — TURN_RATE
     * scaled by the hull's own `turn` **and** by what the turbo leaves of it — and is then held
     * to RUDDER_LEAD of where the ship has really got to, so releasing the key stops the turn
     * instead of unwinding a debt. Amidships the course is simply left where it was, which is a
     * ship holding its heading.
     *
     * The speed term is the same pair of calls `advance()` makes — `hullSpeed` then `turnScaleOf`
     * — and it has to be here as well as there: a course winding round faster than the hull can
     * follow would sit pinned at RUDDER_LEAD for as long as the key is held, so lighting the turbo
     * mid-turn would snap the wanted heading ahead of the boat rather than easing the turn out.
     * Lit, the rudder is simply slower, which is what a captain should feel.
     *
     * It reads the **wind** for the same reason, now that the wind is half of how fast a hull is
     * going (design/Wind.md). A rudder scaled against a speed the arena does not agree
     * with is the drift this whole arrangement exists to avoid — so both ends run the one function
     * off the one patch, and the answer is the same on both.
     */
    private _workRudder(deltaSeconds: number): void {
        if (this._rudder === 0) return;

        const speed = hullSpeed(
            this._fitout,
            this._turboLit,
            this._connection.wind,
            this._predicted.heading
        );
        this._targetHeading += this._rudder * TURN_RATE * this._fitout.turn * turnScaleOf(this._fitout, speed) * deltaSeconds;

        const lead = shortestAngle(this._predicted.heading, this._targetHeading);
        if (Math.abs(lead) > RUDDER_LEAD) this._targetHeading = this._predicted.heading + Math.sign(lead) * RUDDER_LEAD;
    }

    /**
     * Fires one gun, if the trigger is down and there is a gun to fire it with.
     *
     * **The system picks the gun, not the player.** Whichever loaded cannon is pointing closest
     * to the aim goes off — `Battery.pick` in shared/Aiming.ts — so a captain aims at a ship and
     * the deck sorts out whose turn it is. Firing the closest one first is also what empties a
     * battery in a sensible order: the gun already on target, then the one still coming round.
     *
     * The trigger is read as **held or freshly pressed**: the first covers a button the player
     * is leaning on, the second a click that came and went inside one frame. See `_firePressed`.
     *
     * Two clocks gate it and they are different questions. `_gunReadyAt` is *that gun's* reload,
     * which the server keeps its own copy of; `_triggerReadyAt` is the whole ship's cadence, and
     * is the client's alone — the arena would happily take a full broadside in one frame, and
     * the reason it does not is here. See TRIGGER_INTERVAL.
     */
    private _workTrigger(): void {
        // Consumed whether or not it is acted on: a latched press is worth one look at the
        // battery, not a standing order that survives until a gun happens to be loaded.
        const pressed = this._firePressed;
        this._firePressed = false;

        if ((!this._fireHeld && !pressed) || this._clock < this._triggerReadyAt) return;

        const view = this._views.get(this._connection.sessionId);
        if (!view) return;

        const aim = shortestAngle(this._predicted.heading, this._aimHeading);
        const loaded = (gun: Cannon): boolean => this._clock >= (this._gunReadyAt.get(gun.index) ?? 0);
        const cannon = view.boat.battery.pick(view.boat.roster, aim, loaded);
        if (!cannon) return;

        // Predicted from the press, and corrected in `_confirmReload` when the arena's own
        // broadcast comes back. Optimistic first so the strip and the next trigger pull answer
        // immediately; conservative afterwards so neither runs ahead of the server.
        this._gunReadyAt.set(cannon.index, this._clock + cannon.reload);
        this._triggerReadyAt = this._clock + TRIGGER_INTERVAL;
        this.onFired?.(cannon);

        // Nothing is drawn here: the ball appears when the broadcast comes back, which on a
        // local arena is the same frame. The server owns the muzzle, the bearing and the reload,
        // and this request is only the name of a gun. See design/Networking.md.
        this._connection.fire(cannon.index);
    }

    /**
     * Creates a `Boat` for every ship the server knows about, and drops the ones it does not.
     *
     * With **one exception**: this client's own hull, once it has been sunk, is kept for as long
     * as the captain is held at sea to watch it. It is out of `ships` like any other departed
     * ship, so without this it would be destroyed on the very frame there is finally something
     * worth looking at. Everybody else's sinking still takes their boat off the water at once —
     * a wreck only the person who died can see is a memorial, and one everybody can see would be
     * a hull the arena says is not there. See `_checkSunk`.
     */
    private _syncViews(): void {
        const ships = this._connection.ships;
        if (!ships) return;

        ships.forEach((ship, sessionId) => {
            if (this._views.has(sessionId)) return;

            const boat = new Boat(resolveFitout(loadoutOf(ship), ship.level));
            // The local boat needs no label — it is the one in the middle of the screen.
            boat.name = sessionId === this._connection.sessionId ? "" : ship.name;
            boat.heading = ship.heading;
            this._shipsLayer.addChild(boat.container);

            this._views.set(sessionId, { boat, x: ship.x, y: ship.y, heading: ship.heading, rig: rigOf(ship) });
        });

        for (const [sessionId, view] of this._views) {
            if (ships.get(sessionId)) continue;
            if (sessionId === this._connection.sessionId && this._sunkAt !== null) continue;
            view.boat.destroy();
            this._views.delete(sessionId);
        }
    }

    /**
     * Refits every boat whose ship has changed since the last frame, which is how growth reaches
     * the water: a level makes the hull visibly bigger and its two guns better, on the same frame
     * the patch lands.
     *
     * The **local** ship's refit also rebuilds two things nothing else owns: the handling the
     * prediction runs on, and the battery strip in the header. Both come out of the same fitout
     * the deck was built from, so a gun on screen, the arc it can swing through and a muzzle on
     * the server are one answer rather than three. See design/Evolution.md.
     */
    private _syncRigs(): void {
        const ships = this._connection.ships;
        if (!ships) return;

        for (const [sessionId, view] of this._views) {
            const ship = ships.get(sessionId);
            if (!ship) continue;

            const rig = rigOf(ship);
            if (rig === view.rig) continue;

            view.rig = rig;
            const fitout = resolveFitout(loadoutOf(ship), ship.level);
            view.boat.fitout = fitout;

            if (sessionId !== this._connection.sessionId) continue;
            this._fitout = fitout;
            // A bigger boat is a wider view. Only the target moves here — `_workZoom` walks the
            // camera onto it over about a second, so growing reads as the sea opening out rather
            // than as the screen changing scale. See design/Ships.md.
            this._targetZoom = cameraZoomOf(fitout);
            this.onBattery?.(rosterFor(fitout));
        }
    }

    /** Feeds the growth panel the player's own ship, or takes it down when there is not one. */
    private _updateGrowth(): void {
        const ship = this._connection.ships?.get(this._connection.sessionId);
        this._growth.visible = Boolean(ship) && this._hasSpawned;
        if (!ship) return;

        this._growth.update(ship.score, ship.level);
        // The header's doubloon count, which is the arena's own number rather than anything this
        // client works out: the purse is state like the score, so there is nothing to predict.
        // Latched on the way past, because `destroy` will want it after the ship has gone.
        this._purse = ship.purse;
        this.onPurse?.(ship.purse);
    }

    /**
     * Keeps the player's own boat predicted, and folds the result back into the sea.
     *
     * The prediction itself is the SDK's rollback loop, built once on first sight of our own hull:
     * every helm command is applied the instant it is sent, and when the arena's answer arrives the
     * hull is rewound onto it and the commands it has not acknowledged yet are replayed on top.
     * That replaced a soft ease toward a patch-old position, which at real latency read as the boat
     * being tugged backwards every time a patch landed. See design/Networking.md.
     *
     * What is left here is the two ends of it: seeding, and folding the continuous pose back into
     * `[0, ARENA_SIZE)` for everything that draws.
     */
    private _advanceLocal(): void {
        const ship = this._connection.ships?.get(this._connection.sessionId);
        if (!ship) return;

        // Predicted from the same rule the server charges by, against a score that is one
        // patch old. Being briefly wrong about the last coin is what reconciliation is for.
        this._turboLit = this._turboHeld && canTurbo(ship.score);

        if (!this._hasSpawned) {
            // First sight of our own hull: take the server's spawn wholesale, and hold
            // the course it gave us so the boat does not immediately swing north.
            this._predicted = { x: ship.x, y: ship.y, heading: ship.heading };
            this._targetHeading = ship.heading;
            // And the guns start laid dead ahead rather than swinging north out of a zero the
            // player never asked for. The pointer takes them over on its first move.
            this._aimHeading = ship.heading;
            this._cameraX = ship.x;
            this._cameraY = ship.y;
            this._hasSpawned = true;
            if (this._status) this._status.visible = false;
            this._buildReconciler(ship);
            return;
        }

        // The arena replaces a ship wholesale when a captain respawns, and the reconciler is pinned
        // to the instance it was built on. Rebuilt rather than reseeded, because a stale handle
        // would quietly predict a hull nobody is sailing.
        if (this._reconciler && this._reconciler.world.truth.ship !== ship) this._buildReconciler(ship);

        const pose = this._reconciler?.pose();
        if (!pose) return;

        // **The one place the sea is folded.** Everything above this line — the step, the replay,
        // the correction — runs in coordinates that accumulate and never wrap, which is what keeps
        // the SDK's arithmetic away from the seam. Everything below draws in the sea's own space.
        this._predicted.x = wrapCoord(pose.x);
        this._predicted.y = wrapCoord(pose.y);
        this._predicted.heading = pose.heading;
    }

    /**
     * Builds the rollback prediction for `ship`, seeded where the arena says it is.
     *
     * The world is deliberately shaped so the SDK never sees a wrapped number: `hull` accumulates,
     * `truth` is nested so the decoded ship stays opaque, `adopt` bridges the two across the seam
     * with `wrapDelta`, and the heading is put back on its continuous representative after every
     * step. See `PredictedWorld` and design/Networking.md, "On the client".
     */
    private _buildReconciler(ship: Ship): void {
        const predict = this._connection.predict;
        const input = this._connection.input;
        if (!predict || !input) return;

        this._reconciler = predict.sim<Data<HelmInput>, PredictedPose, PredictedWorld>({
            input,
            world: {
                hull: { x: ship.x, y: ship.y, heading: ship.heading },
                truth: { ship }
            },

            step: (ctx, world, command) => {
                // **Frozen per tick, because a replay cannot re-derive either of them.** The wind
                // has veered and the score has moved by the time an old command is replayed, and
                // reproducing the arena's trajectory means using what was true when the command
                // was first applied — which is exactly what `memo` is for.
                const wind = ctx.memo("wind", () => this._connection.wind) ?? this._connection.wind;
                const lit = ctx.memo("turbo", () => command.turbo && canTurbo(world.truth.ship?.score ?? 0)) ?? false;

                // The arena's own loop, exactly: one command applied across `subSteps` integrations
                // of `subDt`, both numbers advertised by the server rather than agreed twice.
                for (let i = 0; i < ctx.subSteps; i++) {
                    const before = world.hull.heading;
                    advance(world.hull, command.heading, ctx.subDt, lit, this._fitout, wind, false);
                    // `advance` folds the heading into (-PI, PI]; put it back on the representative
                    // it started from, so the whole predicted pose is continuous and the SDK's lerp
                    // never sweeps the long way round the compass. The sea's seam and the compass's
                    // are the same problem, and this is the same answer.
                    world.hull.heading = before + shortestAngle(before, world.hull.heading);
                }
            },

            adopt: (world) => {
                const truth = world.truth.ship;
                if (!truth) return;

                // The whole of the bridge between a wrapped arena and an unwrapped prediction, and
                // the reason nothing else in this file has to think about the seam.
                world.hull.x += wrapDelta(world.hull.x, truth.x);
                world.hull.y += wrapDelta(world.hull.y, truth.y);
                world.hull.heading += shortestAngle(world.hull.heading, truth.heading);
            },

            pose: (world) => ({ x: world.hull.x, y: world.hull.y, heading: world.hull.heading }),

            smoothMs: RECONCILE_SMOOTH_MS,
            snap: RECONCILE_SNAP,
            // Dev only: says so, loudly and throttled, when the arena and the prediction disagree
            // by more than a hull's length. It costs no traffic — the correction already *is* the
            // divergence — and it is the first thing that will notice `advance` drifting apart.
            warnOnDivergence: import.meta.env?.DEV ? DIVERGENCE_WARN : undefined
        });
    }

    /** Eases everyone else onto their latest server position. No prediction — just smoothing. */
    private _advanceRemotes(deltaSeconds: number): void {
        const ships = this._connection.ships;
        if (!ships) return;

        const blend = Math.min(1, REMOTE_LERP_RATE * deltaSeconds);

        for (const [sessionId, view] of this._views) {
            if (sessionId === this._connection.sessionId) continue;

            const ship = ships.get(sessionId);
            if (!ship) continue;

            const errorX = wrapDelta(view.x, ship.x);
            const errorY = wrapDelta(view.y, ship.y);

            if (Math.hypot(errorX, errorY) > REMOTE_SNAP) {
                view.x = ship.x;
                view.y = ship.y;
                view.heading = ship.heading;
                continue;
            }

            view.x = wrapCoord(view.x + errorX * blend);
            view.y = wrapCoord(view.y + errorY * blend);
            view.heading += shortestAngle(view.heading, ship.heading) * blend;
        }
    }

    /**
     * Walks the camera onto the zoom this ship's hull asks for. See ZOOM_RATE.
     *
     * The last step is a snap rather than another fraction of the gap, because the ease is
     * asymptotic: without it the camera spends the rest of the battle laying out every
     * world-space layer to chase a difference nobody can see.
     */
    /**
     * The zoom actually drawn at: what the ship asks for, held above the floor that keeps the
     * **torus** honest.
     *
     * Boats are drawn at `camera + wrapDelta(camera, ship)`, and `wrapDelta` answers in
     * `(-ARENA_SIZE/2, ARENA_SIZE/2]` — so a hull genuinely a little more than half an arena away
     * resolves to the *near* side. That is not wrong in itself; it is how the seam is hidden.
     * It only becomes wrong when the wrapped copy is **on screen**, which is when the player can
     * see a ship drawn at a position it is not at.
     *
     * The bound falls straight out of that. The worst case is a hull just past the halfway line,
     * which draws at `(ARENA_SIZE / 2) * zoom` from the centre; it is off screen as long as that
     * clears half the viewport, so
     *
     * ```
     * max(width, height) / zoom  <=  ARENA_SIZE
     * ```
     *
     * per axis, since x and y wrap independently. `WRAP_MARGIN` keeps a tenth in hand for a hull's
     * own drawn extent, which sticks out past its centre.
     *
     * **It held for free until captains started growing.** Every hull was one size, so every
     * client sat at the one zoom its preset authored. At the shipped ceiling the biggest boat asks
     * for about 0.33, which on a 2560px window is past the line.
     *
     * A captain who reaches the floor simply stops getting a wider view. That is the right
     * failure: the alternative is ships appearing on the wrong side of the screen near the seam,
     * and no amount of camera easing makes that read as anything but a bug.
     *
     * **It is a guard, not a design.** A catalogue that reaches it in ordinary play is a catalogue
     * whose scale ceiling is too high for its arena, and the fix for that is `mapSize` or the
     * ceiling in the dashboard — not this clamp. See design/Ships.md.
     */
    private _framedZoom(): number {
        const floor = Math.max(this._width, this._height) / (ARENA_SIZE * WRAP_MARGIN);
        return Math.max(this._zoom, floor);
    }

    private _workZoom(deltaSeconds: number): void {
        const gap = this._targetZoom - this._zoom;
        if (gap === 0) return;

        this._zoom = Math.abs(gap) < ZOOM_EPSILON ? this._targetZoom : this._zoom + gap * Math.min(1, ZOOM_RATE * deltaSeconds);
        this._applyZoom();
    }

    /**
     * Puts the world layer at the current zoom, and tells everything drawn inside it how big the
     * viewport is **in world pixels**.
     *
     * The whole scheme is one `scale` and one changed number. Those layers already worked in a
     * space whose origin is the top-left of the viewport and whose centre is the camera, so
     * handing them a viewport of `screen / zoom` and scaling their common parent by `zoom` puts
     * the player's hull back on the exact middle pixel and everything else at its true distance
     * from it — without a single one of them learning that a zoom exists. Their culling comes out
     * right for free, because they cull against the same viewport they were given.
     *
     * **The sea is deliberately not in it.** `Water` is a screen-space plate: its overscan, its
     * `filterArea` and its 80px displacement are all measured in the pixels the filter actually
     * runs on, and scaling the container would leave the warp pulling in from outside a plate
     * that no longer covers the screen. Waves are an abstract texture with no world size to be
     * wrong about, so they keep their tuned look at every zoom and only `scroll` has to convert.
     * The school in them is the opposite case and does scale: a shark is authored in the same
     * pixels a hull is, so it has to shrink with the hulls or a galleon would sail past a fish
     * twice its size. See design/Water.md.
     */
    private _applyZoom(): void {
        const zoom = this._framedZoom();

        this._world.scale.set(zoom);
        this._seaLife?.container.scale.set(zoom);
        this._wakeField?.setZoom(zoom);

        this._viewWidth = this._width / zoom;
        this._viewHeight = this._height / zoom;

        this._lootField.resize(this._viewWidth, this._viewHeight);
        this._shotField.resize(this._viewWidth, this._viewHeight);
        this._plunderField.resize(this._viewWidth, this._viewHeight);
        this._seaLife?.resize(this._viewWidth, this._viewHeight);
        this._wakeField?.resize(this._viewWidth, this._viewHeight);
        this._sprayField.resize(this._viewWidth, this._viewHeight);
    }

    /** The camera is the predicted boat, and the sea scrolls by however far it moved. */
    private _moveCamera(deltaSeconds: number): void {
        if (!this._hasSpawned) return;

        const dx = wrapDelta(this._cameraX, this._predicted.x);
        const dy = wrapDelta(this._cameraY, this._predicted.y);

        this._cameraX = this._predicted.x;
        this._cameraY = this._predicted.y;

        // The waves are measured from the camera, so this is the one place that can move them on:
        // dx and dy are how far the water slid past, already through `wrapDelta`, so the seam is
        // folded out before the sea ever sees it. See src/scripts/sea/Waves.ts.
        this._waves.update(deltaSeconds, this._connection.wind, dx, dy);
        this._water?.setCamera(this._cameraX, this._cameraY, this._zoom);
        // The school is in that water, so it takes the same offset — but it is scaled with the
        // world, so it takes the world one, unconverted. Measured with wrapDelta above, so
        // crossing the seam drifts the animals a few pixels rather than flinging them the width
        // of the arena.
        this._seaLife?.scroll(dx, dy);
        // And the foam with it, in the same world pixels. A mark is a point on the water, so
        // leaving it pinned to the camera would drag the whole trail along behind the player.
        this._wakeField?.scroll(dx, dy);
        this._sprayField.scroll(dx, dy);
    }

    /**
     * Where a ship is **being drawn** right now and how long its hull is, or `null` if this
     * client is not drawing one for that `sessionId`.
     *
     * The drawn position rather than the server's, and that is the point of it: the plunder
     * coins are chasing a hull the player is watching, so they have to chase the *predicted*
     * local boat and the *smoothed* remote one, or a burst would land beside its target on
     * every screen — and the payout label rides the same answer, so it stays over the deck as
     * the ship sails out from under it.
     *
     * The length comes off the boat's own fitout rather than being guessed at, because the
     * label is lifted in hull lengths the way the nameplate under the boat is dropped in them:
     * a Galleon's number has to clear a Galleon.
     */
    private readonly _locate = (sessionId: string): Hull | null => {
        const view = this._views.get(sessionId);
        if (!view) return null;

        const length = view.boat.fitout.size.length;
        if (sessionId !== this._connection.sessionId) return { x: view.x, y: view.y, length };

        return this._hasSpawned ? { x: this._predicted.x, y: this._predicted.y, length } : null;
    };

    /**
     * Places every hull relative to the camera. `wrapDelta` is what hides the seam: a boat
     * just over the west edge is drawn a few pixels west of a camera sitting on the east
     * edge, rather than most of an arena away.
     */
    /**
     * Draws the wave field as a grid of dots: bigger and paler on a crest, smaller and darker in a
     * trough. Ugly on purpose, off by default, and the only honest way to look at the half of the
     * sea that does not live on the GPU.
     *
     * What it exists to prove, which no test can see: **the CPU field and the shader agree.** The
     * dots come out of `waveHeightAt` and the water under them out of `WAVE_GLSL`, and those are
     * one formula written twice (src/scripts/sea/Waves.ts). Put this up and the white dots have to
     * sit on the bright water. When they drift, hulls are riding a wave nobody can see — which is
     * exactly the bug that would otherwise be found by a player and not by us.
     *
     * It rebuilds a `Graphics` every frame, which is the one thing design/Water.md's performance
     * note says not to do. That is affordable here precisely because it is not on: a development
     * overlay that costs a frame while it is up costs nothing at all the rest of the time.
     */
    private _drawWaveDebug(): void {
        this._waveDots.clear();

        // A grid fixed to the screen, because the sea is measured from the camera: a dot is the
        // field sampled at that offset, and the crests slide under it as the boat moves.
        const centreX = this._viewWidth / 2;
        const centreY = this._viewHeight / 2;

        for (let y = 0; y < this._viewHeight + WAVE_DOT_SPACING; y += WAVE_DOT_SPACING) {
            for (let x = 0; x < this._viewWidth + WAVE_DOT_SPACING; x += WAVE_DOT_SPACING) {
                const height = waveHeightAt(this._waves.trains, x - centreX, y - centreY);
                const radius = Math.abs(height) * WAVE_DOT_RADIUS;
                if (radius < 0.5) continue;

                this._waveDots.circle(x, y, radius).fill({ color: height > 0 ? 0xffffff : 0x00204a, alpha: 0.75 });
            }
        }
    }

    private _placeBoats(deltaSeconds: number): void {
        // The world viewport, not the screen: `_world` is scaled by the zoom on the way out, so
        // half of this lands on the middle pixel of the window at any hull size.
        const centreX = this._viewWidth / 2;
        const centreY = this._viewHeight / 2;
        const localId = this._connection.sessionId;
        const ships = this._connection.ships;

        for (const [sessionId, view] of this._views) {
            const isLocal = sessionId === localId;
            // This client's own hull, after it went down. There is no ship behind it any more, so
            // everything the wire would say about it — the turbo, the aim, whether it is a ghost —
            // has no answer, and it holds the pose it sank on while it settles. See `_checkSunk`.
            const wrecked = isLocal && this._sunkAt !== null;
            const x = isLocal ? this._predicted.x : view.x;
            const y = isLocal ? this._predicted.y : view.y;
            const heading = isLocal ? this._predicted.heading : view.heading;

            // The local trail answers to the button, so it thickens on the same frame the
            // player presses it; everyone else's comes off the wire like their position does.
            const boosting = isLocal ? this._turboLit : (ships?.get(sessionId)?.turbo ?? false);
            // Off the wire for everyone, the local hull included — this client's own boat is
            // never a ghost while it is being drawn, because a captain who dropped out is not
            // watching. The setter is a no-op unless the answer changed. See design/Battle.md.
            //
            // A wreck is washed by the other setter instead, which goes further and takes the
            // hull all the way under. Both write the same tint and alpha, so exactly one of them
            // may run on any boat — which is why this is an else and not a second line.
            if (wrecked) view.boat.foundered = this._founderedness();
            else view.boat.ghost = ships?.get(sessionId)?.ghost ?? false;

            const drawnX = centreX + wrapDelta(this._cameraX, x);
            const drawnY = centreY + wrapDelta(this._cameraY, y);
            view.boat.container.position.set(drawnX, drawnY);

            // Every hull leaves a trail, not just this player's — a rival crossing your bow is
            // half of what the effect is for. It is fed the position the boat is *drawn* at, so
            // the seam is already folded out of it and the field never learns the world loops.
            // The size goes with it because the stern offset and the V are measured in hulls.
            const { length: hullLength, beam } = view.boat.fitout.size;
            // A wreck lays neither. Both are marks a hull under way leaves on the water, and this
            // one is not under way — the foam it laid on its last run is already in the field and
            // fades on its own clock, which is exactly the trail the player should be looking at.
            if (!wrecked) {
                this._wakeField?.trail(sessionId, drawnX, drawnY, heading, hullLength, beam, boosting);
                // And what the bow throws, which unlike the wake is not a function of speed alone:
                // it reads the sea in front of the stem to find out whether the bow is burying.
                this._sprayField.spray(sessionId, drawnX, drawnY, heading, hullLength, this._waves.trains, deltaSeconds);
            }
            // `update` drives the wake animation; the heading is assigned rather than
            // turned towards, because the turning already happened — on the server for
            // other boats, in `_advanceLocal` for this one.
            view.boat.update(deltaSeconds);
            view.boat.heading = heading;

            // A wreck's guns and canvas are left exactly where they were when the ball landed:
            // there is no captain to train one and no crew to trim the other, and swinging the
            // battery back to dead ahead as the hull sinks would be the deck tidying itself up.
            // It still rides the sea below, because it is still in the sea.
            if (wrecked) {
                view.boat.ride(this._waves.trains, drawnX - centreX, drawnY - centreY);
                continue;
            }

            // Guns last, and after the heading: an arc is measured from the bow, so training
            // them against a stale heading would drag the whole battery a frame behind the hull.
            // The local ship aims off the pointer, a patch ahead of the arena; everybody else's
            // barrels come off `Ship.aim` the way their position does, and both ends run the
            // same traverse so a rival's gun is drawn where its ball will leave from.
            view.boat.aimAt(isLocal ? this._aimHeading : (ships?.get(sessionId)?.aim ?? heading), deltaSeconds);

            // Canvas last, and after the heading for the reason the guns are: a sail is braced
            // against the bow, so trimming off a stale heading would drag the whole rig a frame
            // behind the hull. Every boat gets the arena's one wind — a rival's sails saying what
            // their ship is doing is most of why this is drawn at all. See design/UI.md.
            view.boat.trimTo(this._connection.wind, deltaSeconds);

            // And the sea under it, off the boat's offset from the camera — which is what the drawn
            // position already is, less the middle of the screen. The same field the shader is
            // running, so a hull sits on the crest it looks like it is sitting on.
            view.boat.ride(this._waves.trains, drawnX - centreX, drawnY - centreY);
        }
    }
}

/**
 * The loadout a ship is sailing, in the shape `shared/Fitout.ts` resolves. Copied out of the
 * schema, because a `Loadout` is a plain object and `Ship.cannons` is an `ArraySchema`.
 *
 * **No validation here, deliberately.** The arena ran `sanitiseLoadout` on these before they
 * reached state, so what a client decodes is already clamped into the catalogue it holds — and
 * if the two ends disagree about that catalogue, `resolveFitout` degrades to the free boat on its
 * own. A second clamp here would only hide a version skew that ought to be visible.
 */
function loadoutOf(ship: Ship): Loadout {
    return {
        preset: ship.preset,
        cannons: [...ship.cannons],
        sailTint: ship.sailTint,
        hullTint: ship.hullTint,
        emblem: ship.emblem,
        emblemTint: ship.emblemTint
    };
}

/**
 * How far the camera sits out for a ship: its preset's framing, opened up by however far it has
 * grown. A ship with no preset at all — a catalogue a client a version behind cannot resolve — is
 * drawn at 1:1 rather than at nothing.
 *
 * **Opened up with the scale**, which is what makes growth read as the sea opening out rather than
 * as the player's own deck filling the screen: a captain who is twice the boat they spawned as
 * would otherwise be looking at twice as much of their own hull at exactly the moment they most
 * need to see who is closing on it. The preset's own `cameraZoom` is its framing at the bottom of
 * the curve, so at level 0 this is exactly what the dashboard authored.
 *
 * **`ZOOM_COMPENSATION`, not a plain divide**, and that is the whole difference between growth a
 * player can see and growth they cannot. Dividing by the scale outright is *exact* compensation:
 * the hull is drawn bigger and the camera pulls back by precisely as much, so a captain's own ship
 * occupies the same pixels at level 20 as it did at level 0 and the only evidence of growing is
 * that other boats got smaller. The exponent — the same 0.85 `defaultCameraZoom` applies across
 * presets, exported from `shared/BoatTree.ts` so there is one number for one rule — gives back
 * most of it and keeps a little: at the shipped ceiling the player's own hull grows about 10% on
 * screen while the sea around it nearly doubles.
 *
 * It is read off the preset rather than lifted onto `Fitout` next to `speed` and `turn`, because
 * it is not the same kind of number: those two are parameters of the integrator both ends run, and
 * this is a property of one client's window. Nothing in `shared/` should have an opinion about it.
 * See design/Ships.md.
 */
function cameraZoomOf(fitout: Fitout): number {
    return (fitout.preset?.cameraZoom ?? 1) / Math.max(0.01, fitout.scale) ** ZOOM_COMPENSATION;
}

/**
 * A ship's loadout and level as one string, for asking "is this still the same ship" cheaply. All
 * of it is small integers, so a compare against last frame's spelling costs less than resolving a
 * fitout would.
 *
 * The **level** is the part that actually changes during a life, and it is what makes a boat grow
 * on the water: the frame a level-up patch lands, this string differs, the fitout is re-resolved
 * and the deck is rebuilt at its new size with better guns on it.
 */
function rigOf(ship: Ship): string {
    return `${ship.preset}|${ship.cannons.join(",")}|${ship.level}`;
}
